import * as UI from "@iyk/ui"
import * as React from "react"

import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import { Res } from "@iyk/vf-server"
import { Effect } from "effect"
import { appConfig } from "../../../app-config.ts"
import { GlobalRoutes } from "../../../react-router/routes.ts"
import { Handler } from "../../../server/handler.ts"
import { Header } from "../../profile/ui/header.tsx"
import { Link } from "../../ui/link.tsx"
import { Layout } from "../ui/layout.tsx"
import type { Route } from "./+types/landing.ts"

export const meta: Route.MetaFunction = ({ data }) => [
  {
    title: `Join | ${appConfig.artist.name}`
  },
  {
    property: "og:title",
    content: `Join | ${appConfig.artist.name}`
  },
  {
    property: "og:type",
    content: "website"
  },
  {
    property: "og:url",
    content: `${data.appUrl}/join`
  },
  {
    property: "og:image",
    content: data.proofConfig.assets.og
  },
  {
    property: "og:width",
    content: "1200"
  },
  {
    property: "og:height",
    content: "630"
  },
  // Twitter
  {
    property: "twitter:card",
    content: "summary_large_image"
  },
  {
    property: "twitter:title",
    content: `Join | ${appConfig.artist.name}`
  },
  {
    property: "twitter:image",
    content: data.proofConfig.assets.og
  },
  {
    property: "twitter:width",
    content: "1200"
  },
  {
    property: "twitter:height",
    content: "630"
  }
]

export const loader = Handler.make("join-landing", ({ sessions, joinProofs, appUrl }) => {
  const joinProofResponse = Effect.fromNullable(appConfig.joinProof).pipe(
    Effect.map((proofConfig) => Res.json({ appUrl, proofConfig })),
    Effect.mapError(() => Res.notFound),
    Effect.merge
  )

  return Effect.gen(function* () {
    const session = yield* sessions.authenticated

    yield* joinProofs.findByUserId(session.user.id)

    return Res.redirect(GlobalRoutes.profile)
  }).pipe(
    Effect.catchTags({
      Unauthorized: () => joinProofResponse,
      JoinProofNotFound: () => joinProofResponse
    })
  )
})

export default function Landing({ loaderData }: Route.ComponentProps) {
  const analytics = useAnalytics()

  React.useEffect(() => {
    analytics.track(Analytics.Event.JoinProofLanded())
  }, [])

  return (
    <Layout>
      <div
        className="absolute inset-x-0 top-16 bottom-0 rounded-t-2xl bg-cover bg-center [mask-image:linear-gradient(to_top,rgba(17,17,17,0),#111111_50%)]"
        style={{ backgroundImage: `url(${loaderData.proofConfig.assets.backgroundImage})` }}
      />
      <Header />
      <footer className="p-4 shrink-0 flex flex-col gap-4 mt-auto relative z-10">
        <span
          className={UI.cx(
            "font-departure-mono text-xs-upper text-grayA-12",
            "px-3 py-2 mx-auto text-center",
            "rounded-md bshadow bshadow-grayA-2",
            "backdrop-blur-md bg-[var(--grayA-1),rgba(255,255,255,0)]"
          )}
        >
          <UI.Typewriter
            typingSpeed={70}
            deletingSpeed={30}
            delayBeforeDelete={1500}
            texts={[
              "Unlock exclusive content",
              `Verify your ${loaderData.proofConfig.name} fandom`,
              `Keep in touch with ${loaderData.proofConfig.name}`
            ]}
          />
          <UI.BlinkingCursor />
        </span>

        <Link
          to={GlobalRoutes.login({ returnTo: GlobalRoutes.join.landing })}
          className={UI.classForButton({ size: "lg", kind: "solid", className: "w-full" })}
        >
          Join
        </Link>
      </footer>
    </Layout>
  )
}
